import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { SortType } from "../../../../../models/SortType";
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Badge,
  Backdrop,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../../../utils/NotificationsContext";
import FilterSidebar from "../../../views/components/filter/FilterSidebar";
import { FilterSidebarMenu, FilterMenuType } from "../../../../../models/FilterMenu";
import { ModuleType } from "../../../../../models/ModuleType";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import UrgentSourcingProductsTable from "../../components/urgent-sourcing/UrgentSourcingProductsTable";
import {
  ProductStatusFilter,
  UrgentSourcingProductFilters,
} from "../../../../../models/UrgentSourcing/UrgentSourcingProductFilter";
import UrgentSourcingAddCatalogProductPopup from "./UrgentSourcingAddCatalogProductPopup";
import {
  ProductImage,
  UrgentSourcingCatalogProductForm,
} from "../../../../../models/UrgentSourcing/UrgentSourcing";
import ImageGalleryPopup from "../../../../../views/components/common/pop-up/ImageGalleryPopup";

const UrgentSourcingProductsListing: React.FC = () => {
  const { userStore, urgentSourcingStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const parseUrlParams = (): UrgentSourcingProductFilters => {
    const parseFilterArray = (param: string | null): FilterValue[] => {
      if (!param) return [];
      return param.split(",").map((item) => {
        const [id, value] = item.split(":");
        return { id: id || "", value: value || "" };
      });
    };

    const productCreatedParam = searchParams.get("product_created");
    const productCreated =
      productCreatedParam === "true"
        ? true
        : productCreatedParam === "false"
        ? false
        : undefined;

    return {
      page_no: parseInt(searchParams.get("page_no") || "1", 10),
      search_text: searchParams.get("search_text") || undefined,
      limit: parseInt(searchParams.get("limit") || "30", 10),
      sort_by: searchParams.get("sort_by") || "created_date",
      sort_type:
        searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
      added_by: parseFilterArray(searchParams.get("added_by")),
      product_created: productCreated,
    };
  };

  const initialParams: UrgentSourcingProductFilters = parseUrlParams();
  const [filters, setFilters] =
    useState<UrgentSourcingProductFilters>(initialParams);
  const [params, setParams] =
    useState<UrgentSourcingProductFilters>(initialParams);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );

  useEffect(() => {
    updateSelectedFilters(initialParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSelectedFilters = (
    appliedFilters: UrgentSourcingProductFilters
  ) => {
    let temp_filters_count = 0;

    if (appliedFilters?.product_created !== undefined) {
      temp_filters_count++;
    }

    if (appliedFilters?.added_by && appliedFilters.added_by.length > 0) {
      temp_filters_count++;
    }

    urgentSourcingStore.setFilterCount(temp_filters_count);
  };

  const handleFilterChange = useCallback(
    (
      key: keyof UrgentSourcingProductFilters,
      value: UrgentSourcingProductFilters[keyof UrgentSourcingProductFilters]
    ) => {
      setFilters((prev: UrgentSourcingProductFilters) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const filterMenu: FilterSidebarMenu[] = useMemo(() => {
    return [
      {
        moduleType: ModuleType.ADMIN,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_admin",
        label: "Approved By",
        placeholder: "Enter Approved Name",
        value: filters.added_by,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("added_by", value),
      },
      {
        moduleType: ModuleType.URGENT_SOURCING,
        fieldType: FilterMenuType.LOCAL_SINGLE_SELECT,
        label: "Product Created",
        placeholder: "Select",
        list: ProductStatusFilter,
        value:
          filters?.product_created !== undefined
            ? ProductStatusFilter.filter(
                (item) =>
                  item.id === (filters.product_created ? "true" : "false")
              )
            : [],
        onChange: (selectedValues: FilterValue[]) => {
          setFilters((prev) => ({
            ...prev,
            product_created:
              selectedValues.length > 0
                ? selectedValues[0].id === "true"
                : undefined,
          }));
        },
      },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleApplyFilters = () => {
    setParams(filters);
    updateSelectedFilters(filters);
    handleFilterToggle();
    urgentSourcingStore.setPopupState("openFilter", false);
  };

  const handleClearFilters = () => {
    const clearedFilters: UrgentSourcingProductFilters = {
      added_by: [],
      product_created: undefined,
      page_no: 1,
      search_text: undefined,
      limit: initialParams.limit,
      sort_by: "created_date",
      sort_type: SortType.DESC,
    };
    setFilters(clearedFilters);
    setParams(clearedFilters);
    updateSelectedFilters(clearedFilters);
    handleFilterToggle();
    urgentSourcingStore.setPopupState("openFilter", false);
  };

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.ADD_PRODUCT_IN_UNIVERSAL_CATALOG)) {
    urgentSourcingStore.setIsFetchingData(true);
    urgentSourcingStore
      .getUrgentSourcingProductsListing(params)
      .then(() => {})
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => {
        urgentSourcingStore.setIsFetchingData(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearch = (query: string) => {
    if (query.trim().length > 0) {
      setSearchTerm(query);
    } else {
      setSearchTerm("");
      setParams((prev: UrgentSourcingProductFilters) => ({
        ...prev,
        search_text: undefined,
        page_no: 1,
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev: UrgentSourcingProductFilters) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev: UrgentSourcingProductFilters) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev: UrgentSourcingProductFilters) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev: UrgentSourcingProductFilters) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleFilterToggle = () => {
    if (urgentSourcingStore.popupState.openFilter) {
      urgentSourcingStore.managePopup("openFilter", "close");
    } else {
      urgentSourcingStore.managePopup("openFilter", "open");
    }
  };

  useEffect(() => {
    const updateQueryParams = () => {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              if (key === "raised_by" || key === "added_by") {
                queryParams.append(
                  key,
                  value
                    .map((item: FilterValue) => `${item.id}:${item.value}`)
                    .join(",")
                );
              } else {
                queryParams.append(
                  key,
                  value.map((item: FilterValue) => item.id).join(",")
                );
              }
            }
          } else if (typeof value === "object" && value.id) {
            queryParams.append(key, value.id);
          } else if (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
          ) {
            queryParams.append(key, String(value));
          }
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        navigate({ search: `?${queryString}` }, { replace: true });
      } else {
        navigate({ search: "" }, { replace: true });
      }
    };
    updateQueryParams();
  }, [params, navigate]);

  const handleAddCatalogProduct = (data: UrgentSourcingCatalogProductForm) => {
    urgentSourcingStore.setIsLoading(true);

    urgentSourcingStore
      .addCatalogProduct(data)
      .then(() => {
        showSuccess("Catalog product added successfully");
        urgentSourcingStore.resetCatalogProductForm();
        urgentSourcingStore.managePopup("openAddProductPopup", "close");
        // urgentSourcingStore.getUrgentSourcingProductsListing(params);
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => {
        urgentSourcingStore.setIsLoading(false);
      });
  };

  return (
    <Container
      sx={{
        paddingTop: 2,
      }}
      maxWidth="xl"
    >
      <Backdrop
        sx={(theme) => ({
          color: theme.palette.customColors.white,
          zIndex: theme.zIndex.drawer + 999999,
        })}
        open={urgentSourcingStore.isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Paper>
        {userStore.hasPermission(
          EmployeeRoles.ADD_PRODUCT_IN_UNIVERSAL_CATALOG
        ) ? (
          <Box
            sx={{
              marginTop: "0.6rem",
              pt: 5,
              pl: 5,
              pr: 5,
              pb: 1,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: window.innerHeight - 110,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <CustomTypography variant="heading3" fontWeight={700}>
                Urgent Sourcing Products Listing
              </CustomTypography>
            </Box>

            <Box
              sx={{
                mt: 3,
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                gap: 4,
              }}
            >
              <CustomTextField
                placeholder={"Search by Lead Id, Product Name , Vendor Name"}
                value={searchTerm}
                onSearch={(query) => handleSearch(query)}
                onKeyDown={handleKeyDown}
                width={"100%"}
              />
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <AppButton
                  variant="grayscale"
                  label="Filter"
                  startIcon={<FilterAltOutlinedIcon />}
                  onClick={() => {
                    handleFilterToggle();
                  }}
                  customStyles={{ width: "130px" }}
                />
                {urgentSourcingStore.filterCount > 0 && (
                  <Badge
                    color="primary"
                    badgeContent={urgentSourcingStore.filterCount}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      transform: "translate(50%, -50%)",
                    }}
                  />
                )}
              </Box>
            </Box>

            {urgentSourcingStore.isFetchingData ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: window.innerHeight - 340,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <UrgentSourcingProductsTable
                maxHeight={window.innerHeight - 340}
                limit={params.limit || urgentSourcingStore.limit}
                page_no={params.page_no || 1}
                total_count={urgentSourcingStore.total_count}
                total_pages={urgentSourcingStore.total_pages}
                sortBy={params.sort_by || "created_date"}
                sortType={params.sort_type as SortType}
                handleViewImagesOfProduct={(productImages: ProductImage[]) => {
                  if (!productImages || productImages.length === 0) {
                    urgentSourcingStore.setSelectedProductImages([]);
                    urgentSourcingStore.setImageGalleryModalOpen(true);
                    return;
                  }
                  urgentSourcingStore.setSelectedProductImages(productImages);
                  urgentSourcingStore.setImageGalleryModalOpen(true);
                }}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
                data={urgentSourcingStore.urgentSourcingProductsResponce}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                Addproduct={async (id: number) => {
                  urgentSourcingStore.updateCatalogProductField(
                    "catalogueId",
                    id
                  );

                  await urgentSourcingStore.getUrgentSourcingCategoriesList();

                  urgentSourcingStore.managePopup(
                    "openAddProductPopup",
                    "open"
                  );
                }}
              />
            )}

            {urgentSourcingStore.popupState.openFilter && (
              <FilterSidebar
                isOpen={urgentSourcingStore.popupState.openFilter}
                items={filterMenu}
                onApply={handleApplyFilters}
                handleClear={handleClearFilters}
                toggleFilter={handleFilterToggle}
              />
            )}
          </Box>
        ) : (
          <PermissionBlockLabel />
        )}
      </Paper>

      <UrgentSourcingAddCatalogProductPopup
        open={urgentSourcingStore.popupState.openAddProductPopup}
        onClose={() => {
          urgentSourcingStore.managePopup("openAddProductPopup", "close");
          urgentSourcingStore.resetCatalogProductForm();
        }}
        onSave={(data) => handleAddCatalogProduct(data)}
        categoriesList={urgentSourcingStore.urgentSourcingCategoriesListResponse.map(
          (category) => ({
            category_id: Number(category.id),
            category_name: category.name,
          })
        )}
      />
      <ImageGalleryPopup
        key={"productImages"}
        open={urgentSourcingStore.openImageGalleryModal}
        onClose={() => urgentSourcingStore.setImageGalleryModalOpen(false)}
        images={
          urgentSourcingStore.selectedProductImages.length > 0
            ? urgentSourcingStore.selectedProductImages.map((image) => ({
                id: image.id,
                src: image.image_url,
              }))
            : []
        }
        title={"Product Images"}
      />
    </Container>
  );
};
export default observer(UrgentSourcingProductsListing);
